/** Descriptive EA estimates from explicitly completed diary days.
 * Ratios and reference lines are not clinical cutoffs or a diagnosis.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { Card, SectionLabel } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { api } from "@/api/client";
import { fmtDate } from "@/lib/dates";

interface EAReading {
  date: string;
  intake_kcal: number;
  exercise_kcal: number;
  ea_kcal_per_kg_ffm: number | null;
  eligible?: boolean;
  eligibility_reasons?: string[];
  status: string;
  has_meal_data: boolean;
}
interface EAWindow {
  end_date: string;
  rolling_3d_avg: number | null;
  rolling_7d_avg: number | null;
  days_with_data: number;
  readings: EAReading[];
}

export function EnergyAvailabilityCard() {
  const { t } = useTranslation();
  const [showMethod, setShowMethod] = useState(false);
  const q = useQuery({
    queryKey: ["energy-availability", "window"],
    queryFn: async () => (await api.get<EAWindow>("/energy-availability/window")).data,
    staleTime: 60_000,
  });
  if (q.isPending) return <Card><SectionLabel>{t("energy.section_label")}</SectionLabel><p className="mt-2 text-sm text-taupe">{t("nutrition.loading")}</p></Card>;
  if (q.isError || !q.data) return <Card><p role="alert">{t("diary_quality.error")}</p><Button size="sm" onClick={() => q.refetch()}>{t("common.retry", "Retry")}</Button></Card>;
  const w = q.data;
  // Fail closed if an older payload lacks the new eligibility contract.
  const eligible = w.readings.filter(r => r.eligible === true && r.ea_kcal_per_kg_ffm != null);
  const avg = eligible.length ? w.rolling_7d_avg : null;
  const needsBodyInputs = w.readings.some(r => r.eligibility_reasons?.some(reason =>
    reason === "body_inputs_provisional" || reason === "ffm_estimated_without_measurement"));
  const ordered = [...w.readings].reverse();
  const maxValue = Math.max(60, ...eligible.map(r => r.ea_kcal_per_kg_ffm ?? 0));
  return (
    <Card>
      <SectionLabel>{t("energy.section_label")}</SectionLabel>
      <p className="mt-2 text-[12px] text-taupe">{t("diary_quality.ea_coverage", { n: eligible.length })}</p>
      {avg == null ? (
        <div className="my-4 text-[13px] text-taupe" data-testid="ea-unavailable">
          <p>{t(needsBodyInputs ? "diary_quality.ea_body_missing" : "diary_quality.ea_empty")}</p>
          {needsBodyInputs ? <div className="mt-2 flex gap-4">
            <Link className="text-amber-dark underline" to="/settings">{t("nav.settings")}</Link>
            <Link className="text-amber-dark underline" to="/body">{t("nav.body")}</Link>
          </div> : <Link className="mt-2 inline-block text-amber-dark underline" to="/nutrition">{t("nutrition.details")}</Link>}
        </div>
      ) : (
        <>
          <div className="my-3"><span className="metric-number text-[30px] text-navy-deep">{avg.toFixed(1)}</span><span className="ml-2 text-[11px] text-taupe">kcal/kg FFM</span></div>
          <p className="text-[11px] text-taupe">{t("diary_quality.ea_avg")}</p>
          <div className="mt-4 flex items-end gap-2" style={{ height: 110 }} aria-label={t("energy.section_label")}>
            {ordered.map(r => {
              const value = r.eligible === true ? r.ea_kcal_per_kg_ffm : null;
              const label = `${fmtDate(r.date)}: ${value == null ? t("diary_quality.unknown") : value.toFixed(1) + " kcal/kg FFM"}`;
              return <div key={r.date} className="flex h-full flex-1 flex-col justify-end text-center" title={label} aria-label={label}>
                <div className={value == null ? "w-full rounded-t border border-dashed border-cream-border" : "w-full rounded-t bg-navy/60"}
                  style={{ height: value == null ? 4 : Math.max(4, value / maxValue * 85) }} />
                <span className="mt-1 text-[10px] text-taupe">{fmtDate(r.date, "EEEEEE")}</span>
              </div>;
            })}
          </div>
        </>
      )}
      <p className="mt-3 border-t border-cream-border pt-3 text-[12px] text-taupe">{t("diary_quality.ea_note")}</p>
      <button className="mt-2 text-[11px] text-amber-dark underline" type="button" onClick={() => setShowMethod(v => !v)} aria-expanded={showMethod}>
        {t(showMethod ? "calorie_balance.hide_methodology" : "calorie_balance.show_methodology")}
      </button>
      {showMethod && <p className="mt-2 rounded bg-cream p-3 text-[12px] text-taupe">{t("diary_quality.ea_method")}</p>}
    </Card>
  );
}
